import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Import images for preview cases
import korindaMooreBefore from "@/assets/gallery/korinda-moore-before.jpg";
import korindaMooreAfter from "@/assets/gallery/korinda-moore-after.jpg";
import patrickBradshawBefore from "@/assets/gallery/patrick-bradshaw-before.jpg";
import patrickBradshawAfter from "@/assets/gallery/patrick-bradshaw-after.jpg";
import anikaVeneersBefore from "@/assets/gallery/anika-veneers-before.jpg";
import anikaVeneersAfter from "@/assets/gallery/anika-veneers-after.jpg";

const Gallery = () => {
  const { t } = useTranslation();

  const previewCases = [
    { 
      id: 1, 
      before: korindaMooreBefore, 
      after: korindaMooreAfter, 
      treatment: t('smileGallery.cases.case1') 
    },
    { 
      id: 3, 
      before: patrickBradshawBefore, 
      after: patrickBradshawAfter, 
      treatment: t('smileGallery.cases.case3') 
    },
    { 
      id: 5, 
      before: anikaVeneersBefore, 
      after: anikaVeneersAfter, 
      treatment: t('smileGallery.cases.case5') 
    },
  ];

  return (
    <section id="gallery" className="py-16 sm:py-20 md:py-24 bg-secondary/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-14 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 px-2">{t('gallery.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('gallery.subtitle')}
          </p>
          <div className="w-16 sm:w-20 h-1 bg-primary mx-auto mt-4"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
          {previewCases.map((caseItem) => (
            <div 
              key={caseItem.id}
              className="group"
            >
              <div className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                {/* Before/After Images */}
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                    <div className="absolute top-3 left-3 z-10 bg-foreground/80 text-white px-3 py-1.5 rounded-full text-xs font-semibold uppercase">
                      {t('smileGallery.before')}
                    </div>
                    <img 
                      src={caseItem.before}
                      alt={`Before ${caseItem.treatment}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={400}
                      height={500}
                      decoding="async"
                      style={{ aspectRatio: '4/5' }}
                    />
                  </div>
                  <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                    <div className="absolute top-3 right-3 z-10 bg-primary text-white px-3 py-1.5 rounded-full text-xs font-semibold uppercase">
                      {t('smileGallery.after')}
                    </div>
                    <img 
                      src={caseItem.after}
                      alt={`After ${caseItem.treatment}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={400}
                      height={500}
                      decoding="async"
                      style={{ aspectRatio: '4/5' }}
                    />
                  </div>
                </div>
                
                {/* Case Information */}
                <div className="p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold text-foreground">
                    {caseItem.treatment}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/smile-gallery">
            <Button size="lg" className="gap-2">
              {t('gallery.seeMore')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
